import React, { useState, useMemo } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useAccounts } from '../../hooks/useAccounts';
import { useAuth } from '../../context/AuthContext';
import ManageCreditCardsModal from './ManageCreditCardsModal';
import { getTodayUTCString } from '../../utils/dateUtils';
import { useAccountStatements } from '../../hooks/useAccountStatements';
import { useFormatting } from '../../context/FormattingContext';
import { formatMoney } from '../../utils/formatting';

interface CreditCardsWidgetProps {
  showViewAllLink?: boolean;
}

const CreditCardsWidget: React.FC<CreditCardsWidgetProps> = ({ showViewAllLink = false }) => {
  const { auth } = useAuth();
  const formatting = useFormatting();
  const { creditCardAccounts, loading, error, refetch } = useAccounts({ includeBalances: true });
  const [manageOpen, setManageOpen] = useState(false);
  const asOfDate = useMemo(() => getTodayUTCString(), []);
  const { statementsByAccountId, errorsByAccountId, loading: statementsLoading } = useAccountStatements(
    {
      accounts: creditCardAccounts,
      token: auth.token,
      asOfDate,
    }
  );

  const totalCreditCardBalance = useMemo(
    () =>
      creditCardAccounts.reduce(
        (sum, account) => sum + (typeof account.currentBalance === 'number' ? account.currentBalance : 0),
        0
      ),
    [creditCardAccounts]
  );

  const totalAvailableCredit = useMemo(
    () =>
      creditCardAccounts.reduce(
        (sum, account) => sum + (typeof account.availableCredit === 'number' ? account.availableCredit : 0),
        0
      ),
    [creditCardAccounts]
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="h6" fontWeight="bold">
          Credit Cards
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {showViewAllLink && (
            <Button size="small" href="/accounts/credit-cards">
              View all
            </Button>
          )}
          <Button size="small" onClick={() => setManageOpen(true)}>
            Manage Cards
          </Button>
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Card balances and available credit.
      </Typography>

      {loading ? (
        <Typography color="text.secondary">Loading accounts...</Typography>
      ) : error ? (
        <Typography color="error.main">{error}</Typography>
      ) : creditCardAccounts.length === 0 ? (
        <Typography color="text.secondary">No credit card accounts found.</Typography>
      ) : (
        <Stack spacing={1.25}>
          {creditCardAccounts.map((account) => (
            <Box
              key={account._id}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}
            >
              <Typography fontWeight={600}>{account.name}</Typography>
                <Typography variant="body2">
                Balance Owed:{' '}
                {formatMoney(typeof account.currentBalance === 'number' ? account.currentBalance : 0, formatting)}
              </Typography>
              {typeof account.creditLimit === 'number' && (
                <Typography variant="body2">Credit Limit: {formatMoney(account.creditLimit, formatting)}</Typography>
              )}
              {typeof account.availableCredit === 'number' && (
                <Typography variant="body2">Available Credit: {formatMoney(account.availableCredit, formatting)}</Typography>
              )}
              {statementsByAccountId[account._id] && (
                <>
                  <Typography variant="body2">
                    Statement: {formatMoney(statementsByAccountId[account._id].statementBalance, formatting)}
                  </Typography>
                  <Typography variant="body2">
                    Due: {statementsByAccountId[account._id].nextPaymentDueDate}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Last close: {statementsByAccountId[account._id].lastStatementCloseDate}
                  </Typography>
                </>
              )}
              {!statementsByAccountId[account._id] && errorsByAccountId[account._id] === 'missing-config' && (
                <Typography variant="caption" color="text.secondary">
                  Set cycle in Manage Cards
                </Typography>
              )}
              {!statementsByAccountId[account._id] && errorsByAccountId[account._id] === 'unavailable' && (
                <Typography variant="caption" color="text.secondary">
                  Statement unavailable
                </Typography>
              )}
              {!statementsByAccountId[account._id] &&
                !errorsByAccountId[account._id] &&
                statementsLoading && (
                  <Typography variant="caption" color="text.secondary">
                    Loading statement...
                  </Typography>
                )}
            </Box>
          ))}

          <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" fontWeight={600}>
              Total Credit Card Balance: {formatMoney(totalCreditCardBalance, formatting)}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              Total Available Credit: {formatMoney(totalAvailableCredit, formatting)}
            </Typography>
          </Box>
        </Stack>
      )}

      <ManageCreditCardsModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        creditCardAccounts={creditCardAccounts}
        token={auth.token}
        onSaved={refetch}
      />
    </Paper>
  );
};

export default CreditCardsWidget;
